// @flow
/**
 * Page Editor component.
 *
 * Root component for handling editing of pages. You may notice that templates
 * are rendered the same way here as they are rendered on the live site. But
 * somehow, when rendered here, controls for editing are rendered in each slot.
 * This component passes down a child context which the `Slot` component looks
 * for to determine whether it is in 'edit mode'. This context is also used for
 * passing callbacks directly to the slots.
 */
import type { Page } from '~/app/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import injectSheet from 'react-jss';
import { useApolloClient } from '@apollo/client';
import { useParams, useHistory, Link } from 'react-router-dom';
import _ from 'shared/lib/gettext';
import { templateSettingsOf } from 'shared/lib/templateSettings';
import { useDispatch, useSelector } from 'react-redux';
import { flashMessage as __flashMessage } from '~/app/actions';
import Templates from 'shared/templates';
import { defaultSlotOf } from 'shared/lib/pageSlots';
import CanvasBoundary from './shell/CanvasBoundary';
import { hasPermissionEditPage } from 'shared/lib/hasPermission';
import EditorContext from 'shared/components/EditorContext';
import cloneDeep from 'lodash/cloneDeep';
import EditComponent from './EditComponent';
import Sidebar from './Sidebar';
import widgetSchemas from 'shared/widgets/schemas';
import EditorRail from './shell/EditorRail';
import OutlinePanel from './shell/OutlinePanel';
import InsertPanel, { INSERT_DRAG_TYPE, aiSection } from './shell/InsertPanel';
import HistoryPanel from './shell/HistoryPanel';
import PulsePanel from './shell/PulsePanel';
import SelectionMarks from './shell/SelectionMarks';
import DeviceFrames, { DEVICES } from './shell/DeviceFrames';
import SectionInspector from './shell/SectionInspector';
import { RowMenu } from '~/app/ui/Menu';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import PublishSheet from './shell/PublishSheet';
import { describeComponent, outlineFor, SLOT_LABELS } from './shell/outline';
import { S, A, DARK } from './shell/shellTokens';
import PageEditorUtil from './PageEditorUtil';
import { toPageInput } from './pageInput';
import useEditorDraft from './useEditorDraft';
import useEditorModal from './useEditorModal';
import useGetPage, { withSchemaMeta } from './composers/useGetPage';
import { GET_PAGE_EDITOR } from './composers/pageEditorCache';
import usePageVersionSummaries from './composers/usePageVersionSummaries';
import usePageVersion from './composers/usePageVersion';
import useUpdatePage from './composers/useUpdatePage';
import loadable from '@loadable/component';
import { io } from 'socket.io-client';
import { getAppURL } from 'shared/lib/clientSettings';
import { WEBSOCKET_MSG_ID } from 'shared/constants';
import uniqBy from 'lodash/uniqBy';
import cx from 'classnames';
import getPageLangDir from 'shared/lib/pageLang';
import moment from 'moment';

const SelectPresets = loadable(() => import(/* webpackChunkName: "SelectPresets" */ './SelectPresets'))

/** Compare preset ids from GraphQL (string) and Mongo (ObjectId). */
const presetIdsMatch = (a, b) => (
  a != null && b != null && String(a) === String(b)
);

type Props = {
  classes: Object,
  page: Page,
  isFetching: boolean,
  history: Object,
  match: {
    params: {
      id: string,
      versionId: string
    }
  },
  widgetPresets: Object[],
  update: (id: string, page: Page, eventType: string) => Promise<*>,
};

// Autosave waits this long after the last change.
export const AUTOSAVE_DELAY_MS = 2500;
// Autosave is off: a draft is kept when someone clicks “Save draft”, and leaving the editor with unsaved
// changes asks first. Set to true to bring autosave back (leaving then saves first instead of asking).
export const AUTOSAVE_ENABLED = false;

// The page as the server stores it (moved to pageInput.js; kept exported here for existing imports).
export { toPageInput };

// Content fingerprint: equal when two drafts would save the same page. Slot order
// doesn't matter to the server, and the draft reorders slots as they change.
const pageSignature = (d) => {
  if (!d || !d.slots || !d.template) return null;
  const input = toPageInput(d);
  return JSON.stringify({ ...input, slots: input.slots.slice().sort((a, b) => (a.slotId < b.slotId ? -1 : 1)) });
};

const INSPECTOR_PREF = 'gx-editor-inspector';
const DEVICE_PREF = 'gx-editor-device';
// Per-browser layout preferences; the editor works the same without storage.
const readPref = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch (e) {
    return fallback;
  }
};
const writePref = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
};
const cssId = value => (window.CSS && window.CSS.escape ? window.CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));
const findCanvasSection = sectionId => document.querySelector(`#pageEditorScrollable [data-gx-component="${cssId(sectionId)}"]`);

/** Parse page updatedAt from GraphQL (unix ms string or ISO date string). */
const parsePageUpdatedAt = (updatedAt) => {
  if (updatedAt == null) return undefined;
  const raw = String(updatedAt);
  if (/^\d+$/.test(raw)) return new Date(Number(raw));
  return new Date(raw);
};

const PageEditor = (props: Props) => {
  const {
    classes: c,
  } = props;

  const { id, versionId } = useParams();
  const history = useHistory();

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));

  // Get user info from redux
  const {
    isBasicUser,
    user
  } = useSelector(mapStateToProps());

  // getPageEditor + version summaries
  const {
    page,
    widgetPresets,
    isFetching
  } = useGetPage({ id });

  const {
    summaries: versionSummaries,
    loading: versionsLoading,
  } = usePageVersionSummaries({ pageId: id });

  const { version: previewVersion, loading: previewLoading } = usePageVersion({
    pageId: id,
    versionId,
  });

  // updatePage
  const { updatePage: update } = useUpdatePage();

  const {
    // Data
    draft,
    activeRevision,

    // Functions
    updateSlot,
    changeVersion,
    changeTemplateSettings,
    changePageSettings,
    changeCategory,
    resetActiveRevision,
    patchDraftMeta,
    reconcileSavedPage,
    undo,
    redo,
    canUndo,
    canRedo,
    historyStep,
    adoptNextServerPage,
    checkpoint,
    cancelCheckpoint,
  } = useEditorDraft(page, previewVersion);

  const lastUpdatedAt = parsePageUpdatedAt(draft?.updatedAt);

  const {
    // Data
    currentModal,
    currentModalProps,

    // Functions
    showEditPresetModal,
    showEditWidgetModal,
    resetModal
  } = useEditorModal();

  // An AI app changing this page through the connector: say so at once (a save now would be refused as outdated).
  const onChangedByApp = useCallback((app) => dispatch(__flashMessage(
    `${app} just changed this page through the Genesis connector. Reload the page to see it; until then, saving here would be refused as outdated.`,
    'DEFAULT',
    15000
  )), [dispatch]);
  const usersOnEdit = useConnectSocketForUsersOnEdit(draft?.path, user?.displayName, id, onChangedByApp);

  // Editor shell: the open tool panel (always Outline when the editor opens), page settings
  // (remembered per browser), the selected section, and the "Removed … Undo" prompt.
  const [panel, setPanel] = useState('outline');
  // A place picked on the canvas for the next section ({ slotId, …, label }), until a section is added there,
  // another one is selected or the Insert panel is left. Bumping searchFocus puts the cursor in its search.
  const [insertAt, setInsertAt] = useState(null);
  const [searchFocus, setSearchFocus] = useState(0);
  const lastClickRef = useRef(null);
  // The row being dragged from the Insert panel, the place a drop goes to, and the line that shows it.
  const dragItemRef = useRef(null);
  const dropPlaceRef = useRef(null);
  const [dropMark, setDropMark] = useState(null);
  const onDragItem = useCallback((item) => { dragItemRef.current = item; if (!item) setDropMark(null); }, []);
  // The right column: page settings ('page'), a section ({ slotId, componentId, subcomponentId }) or closed.
  const [inspector, setInspector] = useState(() => (readPref(INSPECTOR_PREF, '') === 'open' ? 'page' : null));
  const inspecting = inspector && inspector !== 'page' ? inspector : null;
  // After each Edit click the section column shows a brief placeholder (0.2-0.5s, nothing actually loads), so
  // the click visibly changes the column, even for the section already open there.
  const [editLoading, setEditLoading] = useState(false);
  const editLoadingTimer = useRef(null);
  const showEditLoading = () => {
    clearTimeout(editLoadingTimer.current);
    setEditLoading(true);
    editLoadingTimer.current = setTimeout(() => setEditLoading(false), 200 + Math.random() * 300);
  };
  useEffect(() => () => clearTimeout(editLoadingTimer.current), []);
  const [selectedId, setSelectedId] = useState(null);
  const [revealId, setRevealId] = useState(null);
  const [removed, setRemoved] = useState(null);
  // A section deletion waiting for confirmation: { title, run }.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [sheet, setSheet] = useState(null);
  // The publish sheet: { versionId: null } saves the draft as a new version; a versionId asks for review of it.
  const [publishing, setPublishing] = useState(null);
  const nameOf = useCallback(component => describeComponent(component, widgetPresets || [], widgetSchemas).title, [widgetPresets]);
  const [device, setDevice] = useState(() => {
    const saved = readPref(DEVICE_PREF, '');
    return DEVICES[saved] ? saved : null;
  });
  const showDevice = (next) => { setDevice(next); writePref(DEVICE_PREF, next || ''); };
  // When the server last took a change (save or restore): device frames reload then.
  const [serverChangedAt, setServerChangedAt] = useState(null);
  const openPanel = (next) => { setPanel(next); if (next !== 'insert') setInsertAt(null); };
  const togglePageSettings = () => setInspector((current) => {
    const next = current === 'page' ? null : 'page';
    writePref(INSPECTOR_PREF, next ? 'open' : '');
    return next;
  });
  const inspectSection = (slotId, componentId, subcomponentId = null) => {
    if (componentId) setInspector({ slotId, componentId, subcomponentId });
  };
  const outline = useMemo(
    () => (draft ? outlineFor(draft.slots, widgetPresets || [], widgetSchemas) : []),
    [draft, widgetPresets]
  );
  const findOutlineItem = (itemId) => {
    for (const group of outline) {
      const item = group.items.find(i => i.id === itemId);
      if (item) return item;
    }
    return null;
  };

  // Name the tab after the page, so several open editors (and an AI assistant's tabs) can be told apart.
  const pageTitle = page?.title;
  useEffect(() => {
    if (pageTitle) document.title = `${pageTitle} · ${versionId ? 'Earlier version' : 'Page editor'} · Genesis`;
  }, [pageTitle, versionId, id]);

  // Scroll a section into view once it's on the canvas (after an insert, or picked in the outline).
  useEffect(() => {
    if (!revealId) return undefined;
    const frame = requestAnimationFrame(() => {
      const el = findCanvasSection(revealId);
      const scroller = document.getElementById('pageEditorScrollable');
      if (el && scroller) {
        // Leave room above it for the selection label.
        const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 48;
        scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
      setRevealId(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [revealId, draft]);

  // Update the draft with the provided slot, and clear any modals.
  // Editing a component changes its key (props/_id), which remounts that widget;
  // capture and restore the content scroll position so the view doesn't jump.
  const updateSlotState = (slot) => {
    const scrollEl = document.getElementById('pageEditorScrollable');
    const savedScrollTop = scrollEl ? scrollEl.scrollTop : null;

    updateSlot(slot);
    resetModal();

    if (savedScrollTop) {
      // Restore after the remount-driven re-render + paint (two frames covers the
      // transient height collapse that would otherwise clamp scrollTop to 0).
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = document.getElementById('pageEditorScrollable');
        if (el && el.scrollTop !== savedScrollTop) {
          el.scrollTop = savedScrollTop;
        }
      }));
    }
  };

  // Updates the component in the draft
  const handleSaveComponent = (nextComponent: Object) => {
    const {
      slotId, componentPosition, subcomponentId
    } = currentModalProps;

    let slot = draft.slots.find(s => s.slotId === slotId);

    const previousId = (draft.slots.find(s => s.slotId === slotId).components[componentPosition] || {})._id;
    slot = subcomponentId ?
      PageEditorUtil.handleSaveSubcomponent(slot, nextComponent, componentPosition, subcomponentId) :
      PageEditorUtil.handleSaveComponent(slot, nextComponent, componentPosition);

    updateSlotState(slot);

    // The full editor gives the section a new id: keep the selection and inspector on it.
    const savedId = (slot.components[componentPosition] || {})._id;
    if (savedId && previousId && savedId !== previousId) {
      if (selectedId === previousId) setSelectedId(savedId);
      if (inspecting && inspecting.componentId === previousId) setInspector({ ...inspecting, componentId: savedId });
    }
    // A nested section gets a new id too; its inspector can't follow it.
    if (subcomponentId && inspecting && inspecting.subcomponentId === subcomponentId) setInspector(null);
  };

  // Edit (the section's menu on the canvas) opens it in the inspector.
  const handleEdit = (slot, componentPosition, subcomponentId) => {
    const component = (draft.slots.find(s => s.slotId === slot.slotId) || slot).components[componentPosition];
    if (!component) return;
    setSelectedId(component._id);
    inspectSection(slot.slotId, component._id, subcomponentId || null);
    showEditLoading();
  };

  // Show the widget preset selection modal
  const handleUsePresets = (slot, componentPosition) => {
    const { widgetId, presetId } = draft.slots.find(s => s.slotId === slot.slotId).components[componentPosition];
    const filteredPresets = widgetPresets.filter(p => p.widgetId === widgetId);
    const initialPresetId = filteredPresets.some(p => presetIdsMatch(p._id, presetId)) ? presetId : null;

    if (filteredPresets.length > 0) {
      showEditPresetModal(slot.slotId, componentPosition, widgetId, initialPresetId);
    } else {
      // On the page, not a browser alert (those stop keyboard users and AI assistants).
      flashMessage('There are no presets for this widget yet.', 'ERROR');
    }
  };

  // Update widget to use the selected preset
  const handleChangeWidgetPresets = (presetId) => {
    const { slotId, componentPosition } = currentModalProps;

    const slot = cloneDeep(draft.slots.find(s => s.slotId === slotId));

    slot.components[componentPosition].presetId = presetId || null;

    updateSlotState(slot);
    resetModal();
  };

  // Swaps components on the 'drop' action
  const handleDragAndDrop = (slotId, originalWidgetInfo, targetWidgetInfo) => {
    let slot = draft.slots.find(sl => sl.slotId === slotId);

    slot = PageEditorUtil.handleDragAndDrop(slot, originalWidgetInfo, targetWidgetInfo);

    updateSlotState(slot);
  };

  // Moves the component up/down or left/right
  const handleMove = (_slot, i, direction, subcomponentId) => {
    let slot = draft.slots.find(s => s.slotId === _slot.slotId);

    try {
      slot = subcomponentId ?
        PageEditorUtil.handleMoveSubcomponent(slot, i, direction, subcomponentId) :
        PageEditorUtil.handleMoveComponent(slot, i, direction);
    } catch (e) {
      // Already first or last: say so rather than failing silently.
      flashMessage(`It’s already the ${direction === 'UP' ? 'first' : 'last'} one here.`, 'ERROR');
      return;
    }

    updateSlotState(slot);
  };

  // Copys component to the next available location
  const handleCopy = (_slot, i, subcomponentId) => {
    let slot = draft.slots.find(s => s.slotId === _slot.slotId);

    slot = subcomponentId ?
      PageEditorUtil.handleCopySubcomponent(slot, i, subcomponentId) :
      PageEditorUtil.handleCopyComponent(slot, i);

    updateSlotState(slot);
  };

  // Removes the component from the slot
  const handleDeleteComponent = (_slot, i, subcomponentId) => {
    let slot = draft.slots.find(s => s.slotId === _slot.slotId);

    slot = subcomponentId ?
      PageEditorUtil.handleDeleteSubcomponent(slot, i, subcomponentId) :
      PageEditorUtil.handleDeleteComponent(slot, i);

    updateSlotState(slot);
  };

  // The gear menu's Delete on the page asks in the same dialog as "Delete section…" (it used the browser's
  // own confirm box, which stops keyboard users and AI assistants).
  const confirmDeleteFromCanvas = (_slot, i, subcomponentId) => {
    const slot = draft.slots.find(s => s.slotId === _slot.slotId);
    const component = slot && slot.components[i];
    const instance = component && (subcomponentId ? PageEditorUtil.getComponentForId(component, subcomponentId) : component);
    const title = instance ? nameOf(instance) : 'this section';
    setPendingDelete({
      title,
      run: () => {
        handleDeleteComponent(_slot, i, subcomponentId);
        setRemoved({ title, step: historyStep + 1 });
      }
    });
  };

  // Outline actions, by the section's place in its slot.
  const slotOf = slotId => draft.slots.find(s => s.slotId === slotId) || { slotId, components: [] };
  const selectSection = (item) => { setSelectedId(item.id); setRevealId(item.id); setInsertAt(null); };
  const editSection = (item) => { inspectSection(item.slotId, item.id); showEditLoading(); };
  const duplicateSection = (item) => {
    const slot = PageEditorUtil.handleCopyComponent(slotOf(item.slotId), item.index);
    updateSlotState(slot);
    const copy = slot.components[item.index + 1];
    if (copy) { setSelectedId(copy._id); setRevealId(copy._id); }
  };
  const deleteSection = (item) => {
    updateSlotState(PageEditorUtil.handleDeleteComponent(slotOf(item.slotId), item.index));
    if (selectedId === item.id) setSelectedId(null);
    setRemoved({ title: item.title, step: historyStep + 1 });
  };
  const reorderSection = (slotId, from, to) => {
    updateSlotState(PageEditorUtil.handleReorderComponent(slotOf(slotId), from, to));
  };

  // New sections go after the selected one, or at the end of the body: the template's main area, which
  // isn't always called content (shared/lib/pageSlots; the section preview uses the same rule).
  const insertTarget = () => {
    if (insertAt) return { slotId: insertAt.slotId, label: insertAt.label };
    const selected = selectedId && findOutlineItem(selectedId);
    if (selected) return { slotId: selected.slotId, index: selected.index + 1, label: `after “${selected.title}”` };
    const templateId = draft.template && draft.template.templateId;
    const templateSlots = (Templates[templateId] && Templates[templateId].schema && Templates[templateId].schema.slots) || [];
    const slotId = defaultSlotOf(templateSlots.length ? templateSlots.map(sl => sl.slotId) : draft.slots.map(sl => sl.slotId));
    const slot = draft.slots.find(sl => sl.slotId === slotId);
    return { slotId, index: slot ? slot.components.length : 0, label: `at the end of the ${SLOT_LABELS[slotId] || slotId}` };
  };
  // The canvas's "Add widget" buttons, the + between sections and a column's or tab's add button open the
  // Insert panel aimed at that place (one place to pick sections from, with search and previews).
  const chooseFromCanvas = (slotId, componentPosition, subcomponentParentId, subcomponentPosition, insertIndex) => {
    const slot = slotOf(slotId);
    const titleAt = (index) => { const comp = slot.components[index]; return comp ? nameOf(comp) : ''; };
    const area = SLOT_LABELS[slotId] || slotId;
    let label = `at the end of the ${area}`;
    if (subcomponentParentId) label = `inside “${titleAt(componentPosition) || 'this section'}”`;
    else if (Number.isInteger(insertIndex) && insertIndex > 0) label = `after “${titleAt(insertIndex - 1)}”`;
    else if (componentPosition === -1 || insertIndex === 0) label = `at the top of the ${area}`;
    // Mark the spot on the page itself (someone looking at the page doesn't see the panel change): a line
    // where the section will go, across the clicked area, with a small label.
    const clicked = lastClickRef.current;
    let anchor = null;
    if (sheet && clicked && sheet.contains(clicked)) {
      const zone = clicked.closest('[data-gx-insert-at]') || clicked.closest('div') || clicked;
      const r = zone.getBoundingClientRect();
      const s = sheet.getBoundingClientRect();
      let y = r.top + r.height / 2;
      // The top "Add widget" bar: the section goes right below it; the bottom one: right above it.
      if (!subcomponentParentId && !Number.isInteger(insertIndex)) {
        if (componentPosition === -1) y = r.bottom;
        else if (slot.components.length) y = r.top;
      }
      anchor = { top: Math.round(y - s.top), left: Math.round(r.left - s.left), width: Math.round(r.width) };
    }
    setInsertAt({ slotId, componentPosition, subcomponentParentId, subcomponentPosition, insertIndex, label, anchor });
    setPanel('insert');
    setSearchFocus(n => n + 1);
  };
  const insertSection = (component, { edit } = {}) => {
    // A drop from the Insert panel says where; otherwise a place picked on the canvas, if any.
    const dropped = dropPlaceRef.current;
    dropPlaceRef.current = null;
    const at = dropped || insertAt;
    if (insertAt) setInsertAt(null);
    // Basic editors only add inside a column or tab; back to the outline after.
    if (isBasicUser) setPanel('outline');
    if (at && at.subcomponentParentId) {
      const before = slotOf(at.slotId);
      const known = new Set();
      const collect = (co) => { if (!co) return; known.add(co._id); (co.subcomponents || []).forEach(collect); };
      collect(before.components[at.componentPosition]);
      const slot = PageEditorUtil.handleSelectSubcomponent(before, component, at.componentPosition, at.subcomponentParentId, at.subcomponentPosition);
      updateSlotState(slot);
      const parent = slot.components[at.componentPosition];
      const findNew = (co) => {
        if (!co) return null;
        if (!known.has(co._id)) return co;
        return (co.subcomponents || []).reduce((found, sub) => found || findNew(sub), null);
      };
      const added = findNew(parent);
      if (parent) { setSelectedId(parent._id); setRevealId(parent._id); }
      if (!added) return;
      if (edit) showEditWidgetModal(at.slotId, at.componentPosition, added._id);
      else inspectSection(at.slotId, parent._id, added._id);
      return;
    }
    const target = at
      ? { slotId: at.slotId, index: Number.isInteger(at.insertIndex) ? at.insertIndex : (at.componentPosition === -1 ? 0 : slotOf(at.slotId).components.length) }
      : insertTarget();
    const slot = PageEditorUtil.handleSelectComponent(slotOf(target.slotId), component, null, target.index);
    updateSlotState(slot);
    const added = slot.components[target.index];
    if (added) { setSelectedId(added._id); setRevealId(added._id); }
    // A pasted AI section opens the full editor (paste box, checks); others open in the inspector.
    if (edit) showEditWidgetModal(target.slotId, target.index);
    else if (added) inspectSection(target.slotId, added._id);
  };
  // Dragging from the Insert panel onto the page: a line marks the gap between sections it would go into
  // (in any area, an empty one too), and dropping adds it there, like a click after picking that place.
  const gapAt = (clientY) => {
    if (!sheet) return null;
    const sheetTop = sheet.getBoundingClientRect().top;
    let best = null;
    sheet.querySelectorAll('[data-gx-slot]').forEach((slotEl) => {
      const slotId = slotEl.getAttribute('data-gx-slot');
      const comps = slotOf(slotId).components;
      const area = SLOT_LABELS[slotId] || slotId;
      // Its own top-level sections (nested ones carry no data-gx-component).
      const sections = Array.from(slotEl.querySelectorAll('[data-gx-component]')).filter(el => el.closest('[data-gx-slot]') === slotEl);
      const gaps = [];
      if (!sections.length) {
        const r = slotEl.getBoundingClientRect();
        gaps.push({ y: r.top + r.height / 2, index: comps.length, label: `in the ${area}` });
      }
      sections.forEach((el, i) => {
        const index = comps.findIndex(co => co && co._id === el.getAttribute('data-gx-component'));
        if (index < 0) return;
        const r = el.getBoundingClientRect();
        gaps.push({ y: r.top, index, label: i === 0 ? `at the top of the ${area}` : `after “${nameOf(comps[index - 1])}”` });
        if (i === sections.length - 1) gaps.push({ y: r.bottom, index: index + 1, label: `after “${nameOf(comps[index])}”` });
      });
      gaps.forEach((g) => {
        const d = Math.abs(g.y - clientY);
        if (!best || d < best.d) best = { ...g, d, slotId, top: Math.round(g.y - sheetTop) };
      });
    });
    return best;
  };
  const isInsertDrag = e => Boolean(dragItemRef.current) && Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes(INSERT_DRAG_TYPE);
  const onCanvasDragOver = (e) => {
    if (!isInsertDrag(e) || readOnly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const gap = gapAt(e.clientY);
    const title = dragItemRef.current.title;
    setDropMark(prev => (gap && prev && prev.slotId === gap.slotId && prev.index === gap.index && prev.top === gap.top ? prev : gap && { ...gap, title }));
  };
  const onCanvasDragLeave = (e) => {
    // Off the canvas, not just from one of its parts to another.
    if (isInsertDrag(e) && !e.currentTarget.contains(e.relatedTarget)) setDropMark(null);
  };
  const onCanvasDrop = (e) => {
    if (!isInsertDrag(e)) return;
    // Handled here, before the sections' own drop handlers (which move sections) see it.
    e.preventDefault();
    e.stopPropagation();
    const item = dragItemRef.current;
    const gap = readOnly ? null : gapAt(e.clientY);
    dragItemRef.current = null;
    setDropMark(null);
    if (!item || !gap) return;
    dropPlaceRef.current = { slotId: gap.slotId, insertIndex: gap.index, label: gap.label };
    // A custom widget loads its code first; the place waits for it, then is dropped either way.
    Promise.resolve(item.add()).finally(() => { dropPlaceRef.current = null; });
  };

  // The Insert panel can call back after loading a widget's code; by then this render's
  // draft may be stale (another section added, an undo), so it calls the latest insertSection.
  const insertSectionRef = useRef(insertSection);
  insertSectionRef.current = insertSection;
  const insertLatest = useCallback((component, opts) => insertSectionRef.current(component, opts), []);

  // The section in the inspector, found by id (positions change when sections move).
  const inspected = (() => {
    if (!inspecting || !draft) return null;
    const slot = draft.slots.find(sl => sl.slotId === inspecting.slotId);
    const index = slot ? slot.components.findIndex(co => co && co._id === inspecting.componentId) : -1;
    if (index < 0) return null;
    const component = slot.components[index];
    const instance = inspecting.subcomponentId ? PageEditorUtil.getComponentForId(component, inspecting.subcomponentId) : component;
    return instance ? { slot, index, instance } : null;
  })();
  // It was deleted (or undone away): close the inspector.
  useEffect(() => {
    if (inspecting && !inspected) setInspector(null);
  }, [inspecting, inspected]);

  // The inspector follows the selection (a top-level section).
  useEffect(() => {
    if (inspecting && !inspecting.subcomponentId && selectedId && selectedId !== inspecting.componentId) {
      const item = findOutlineItem(selectedId);
      if (item) setInspector({ slotId: item.slotId, componentId: item.id, subcomponentId: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Clicking a section on the canvas selects it in the outline.
  const selectFromCanvas = (e) => {
    // Remembered so a card can show next to an "Add widget" button that was clicked (chooseFromCanvas).
    lastClickRef.current = e.target;
    const el = e.target && e.target.closest ? e.target.closest('[data-gx-component]') : null;
    if (el) setSelectedId(el.getAttribute('data-gx-component'));
  };

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  // idle | saving | saved | error | conflict
  const [saveState, setSaveState] = useState({ status: 'idle' });
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Live edits from the inspector (debounced there). Runs from a timer, so read the latest draft.
  const applyInspectorEdit = (target, props, key) => {
    const current = draftRef.current;
    const slot = current && current.slots.find(sl => sl.slotId === target.slotId);
    if (!slot) return;
    const next = PageEditorUtil.handleUpdateComponentProps(slot, target.componentId, props, target.subcomponentId);
    updateSlot(next, `${target.componentId}:${target.subcomponentId || ''}:${key}`);
  };
  const savingRef = useRef(false);
  // The draft last sent: autosave never sends the same draft twice, so it can't loop.
  const lastSentRef = useRef(null);

  const draftSig = useMemo(() => pageSignature(draft), [draft]);
  const serverSig = useMemo(() => pageSignature(page), [page]);
  const isPreviewing = Boolean(versionId || activeRevision);
  // Without the edit right the server refuses saves (updatePage runs this same check): look, don't change.
  // Basic editors may edit any page, one without a category too.
  const viewOnly = Boolean(page && user && user.roles && !hasPermissionEditPage(user, page.category && page.category._id, 'EDIT'));
  const readOnly = isPreviewing || viewOnly;
  const isDirty = Boolean(!readOnly && draftSig && serverSig && draftSig !== serverSig);

  /**
   * Save the edited page to the database. Autosaves don't log a save event or
   * show a message; a manual save does both.
   */
  const queuedManualSaveRef = useRef(false);
  // The save in flight, so a restore can wait for it instead of racing it.
  const savePromiseRef = useRef(null);
  const apolloClient = useApolloClient();
  // "Outdated" only means the page's timestamp moved. Asking for review, an approval, a decline or a
  // deploy move it without changing the content: the server page, when its content is still the one
  // the draft was built on (baseSig); otherwise null (someone else really saved).
  const unchangedServerPage = async (baseSig) => {
    const fresh = await apolloClient.query({ query: GET_PAGE_EDITOR, variables: { id }, fetchPolicy: 'no-cache' }).catch(() => null);
    const serverPage = fresh && fresh.data && withSchemaMeta(fresh.data.page);
    return serverPage && baseSig && pageSignature(serverPage) === baseSig ? serverPage : null;
  };
  /** Resolves to true when the draft was saved. */
  const savePage = async ({ auto } = {}) => {
    if (!draft) return false;
    if (savingRef.current) {
      // A manual save during an autosave runs right after it, so the click isn't lost.
      if (!auto) queuedManualSaveRef.current = true;
      return false;
    }
    const sentDraft = draft;
    // The server page this draft was built on, to tell a real conflict from a timestamp bump.
    const baseSig = serverSig;
    const eventType = auto ? 'AUTO_SAVE' : 'SAVE_DRAFT';
    const send = at => update(id, { ...toPageInput(sentDraft), lastUpdatedAt: at }, eventType);

    savingRef.current = true;
    lastSentRef.current = sentDraft;
    setIsSavingDraft(true);
    setSaveState({ status: 'saving' });

    let finished;
    let ok = false;
    savePromiseRef.current = new Promise((resolve) => { finished = resolve; });
    try {
      let result;
      try {
        result = await send(parsePageUpdatedAt(sentDraft.updatedAt));
      } catch (e) {
        if (!/outdated/i.test(e.message || '')) throw e;
        const serverPage = await unchangedServerPage(baseSig);
        if (!serverPage) throw e;
        result = await send(parsePageUpdatedAt(serverPage.updatedAt));
      }
      const saved = result?.data?.updatePage;
      if (saved) {
        if (draftRef.current === sentDraft) {
          reconcileSavedPage({
            ...sentDraft,
            ...saved,
            _id: sentDraft._id,
            category: saved.category
              ? { ...sentDraft.category, ...saved.category }
              : sentDraft.category,
          });
        } else {
          // Edited while saving: keep those edits (still unsaved) and take the new timestamp.
          patchDraftMeta(saved.updatedAt);
        }
      }
      setSaveState({ status: 'saved', at: Date.now() });
      setServerChangedAt(Date.now());
      ok = true;
      if (!auto) flashMessage(_('SUCCESS_SAVE_PAGE'));
    } catch (e) {
      const conflict = /outdated/i.test(e.message || '');
      setSaveState({ status: conflict ? 'conflict' : 'error', message: e.message });
      if (!auto) flashMessage(e.message, 'ERROR', 5000);
    } finally {
      savingRef.current = false;
      setIsSavingDraft(false);
      finished();
    }
    return ok;
  };
  const savePageRef = useRef(savePage);
  savePageRef.current = savePage;

  // Runs a manual save that was clicked while another save was in flight.
  useEffect(() => {
    if (!isSavingDraft && queuedManualSaveRef.current) {
      queuedManualSaveRef.current = false;
      savePage({ auto: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavingDraft]);

  const handleSavePage = () => savePage({ auto: false });

  // Autosave a moment after the last change. Paused while the publish sheet is
  // open or a version is being restored: both write the page with the draft's
  // timestamp, and an autosave in between would make one of them "outdated".
  const [restoring, setRestoring] = useState(false);
  useEffect(() => {
    if (!AUTOSAVE_ENABLED || !isDirty || isSavingDraft || publishing || restoring || saveState.status === 'conflict' || draft === lastSentRef.current) return undefined;
    const timer = setTimeout(() => savePage({ auto: true }), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, isDirty, isSavingDraft, publishing, restoring, saveState.status]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  // Leaving the editor inside the app (Back, a link) with changes not saved yet. With autosave on
  // (it waits a moment): save them, then go on, and only ask if that save fails. With autosave off:
  // ask, since saving is the person's call.
  const [leaveBlocked, setLeaveBlocked] = useState(null);
  const unblockLeaveRef = useRef(null);
  const leaveTo = (blocked) => {
    if (unblockLeaveRef.current) { unblockLeaveRef.current(); unblockLeaveRef.current = null; }
    if (blocked.action === 'REPLACE') history.replace(blocked.location); else history.push(blocked.location);
  };
  useEffect(() => {
    if (!isDirty) return undefined;
    const unblock = history.block((location, action) => {
      // Moving within this page's editor (a version, back to the draft) isn't leaving.
      if (location.pathname === `/admin/page/${id}` || location.pathname.startsWith(`/admin/page/${id}/`)) return undefined;
      const blocked = { location, action };
      if (!AUTOSAVE_ENABLED) {
        setLeaveBlocked({ ...blocked, unsaved: true });
        return false;
      }
      (async () => {
        if (savePromiseRef.current) await savePromiseRef.current;
        if (await savePageRef.current({ auto: true })) leaveTo(blocked);
        else setLeaveBlocked(blocked);
      })();
      return false;
    });
    unblockLeaveRef.current = unblock;
    return () => { unblock(); if (unblockLeaveRef.current === unblock) unblockLeaveRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, history, id]);

  // Undo / redo shortcuts, except while typing in a field or with a dialog open.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      const target = e.target;
      const typing = target && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName));
      // Any open dialog (publish sheet, restore or delete confirmation, image picker) keeps the draft as it is behind it.
      const dialogOpen = Boolean(publishing || restoring || document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]'));
      if (typing || currentModal || readOnly || pendingDelete || dialogOpen) return;
      e.preventDefault();
      if (key === 'y' || e.shiftKey) redo(); else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, currentModal, readOnly, pendingDelete, publishing, restoring]);

  // Get the component which will being edited
  const getEditorInstance = () => {
    const { slotId, componentPosition, subcomponentId } = currentModalProps;

    let editorInstance;

    if (slotId) {
      const component = draft.slots.find(s => s.slotId === slotId).components[componentPosition];

      if (subcomponentId) {
        editorInstance = PageEditorUtil.getComponentForId(component, subcomponentId);
      } else {
        editorInstance = component;
      }
    }

    return editorInstance;
  };

  const isLoading = isFetching || versionsLoading || (versionId && previewLoading) || !draft;
  const previewNotFound = versionId && !previewLoading && !previewVersion;

  if (isLoading) {
    return (
      <div className={c.skeletonContainer}>
        <div className={c.skeletonTopBar}>
          <div className={c.skeletonBreadcrumb}>
            <div className={c.skeletonPulse} style={{ width: '60px', height: '14px' }} />
            <span className={c.skeletonSlash}>/</span>
            <div className={c.skeletonPulse} style={{ width: '160px', height: '14px' }} />
          </div>
        </div>
        <div className={c.skeletonBody}>
          <div className={c.skeletonRail} />
          <div className={c.skeletonPanel}>
            {[70, 90, 60, 85, 75, 65].map((w, i) => (
              <div key={i} className={c.skeletonPulse} style={{ width: `${w}%`, height: '14px', marginBottom: '18px' }} />
            ))}
          </div>
          <div className={c.skeletonContent}>
            <div className={c.skeletonPage} />
          </div>
        </div>
      </div>
    );
  }

  if (previewNotFound) {
    return (
      <div className={c.container}>
        <div className={c.top}>
          <div>
            <a className={c.crumb} href="/admin">
              {_('PAGES')}
            </a>
            <span className={c.slash}>/</span>
            <span className={c.title}>
              {page?.title}
            </span>
          </div>
        </div>
        <div className={c.content} style={{ padding: '40px' }}>
          <p>Version {versionId} was not found for this page.</p>
          <a href={`/admin/page/${id}`}>Return to current draft</a>
        </div>
      </div>
    );
  }

  // The site's own rendering of what the server has: the saved draft, or the version being viewed.
  // (The server's path, so an unsaved path change doesn't load a missing page.)
  const viewedVersion = activeRevision || versionId;
  // Viewing an earlier version, nothing on the canvas can be saved, so changes are refused
  // with a pointer to Restore instead of being accepted and then lost.
  const previewOnlyMessage = viewOnly
    ? 'You can view this page. Changing it needs the editor role for its category.'
    : `You’re viewing version ${viewedVersion}. To change it, restore it into your draft from History.`;
  const unlessPreviewing = fn => (...args) => {
    if (readOnly) { flashMessage(previewOnlyMessage); return undefined; }
    return fn(...args);
  };
  const previewUrl = `${page.path}?preview=1&v=${isPreviewing && viewedVersion ? viewedVersion : 'DRAFT'}`;
  const top = (
    // Messages (“Draft saved”, “Published”…) show just below this bar, not over its buttons.
    <header className={c.top} data-toast-below>
      <div className={c.crumbs}>
        {/* In the app, so unsaved changes are saved before leaving (see the leave guard). */}
        <Link className={c.crumb} to="/admin">{_('PAGES')}</Link>
        <span className={c.slash}>/</span>
        <span className={c.title}>{page.title}</span>
        <span className={c.path}>{draft.path}</span>
      </div>
      <div className={c.topRight}>
        <SaveStatus state={saveState} isDirty={isDirty} isPreviewing={isPreviewing} viewOnly={viewOnly} c={c} />
        <button type="button" className={c.topButton} onClick={undo} disabled={!canUndo || readOnly} title="Undo (⌘Z)" aria-label="Undo">
          <i className="material-icons">undo</i>
        </button>
        <button type="button" className={c.topButton} onClick={redo} disabled={!canRedo || readOnly} title="Redo (⇧⌘Z)" aria-label="Redo">
          <i className="material-icons">redo</i>
        </button>
        <OnlineUser
          usersOnEdit={usersOnEdit}
          user={user}
          c={c}
        />
        <span className={c.divider} />
        <div className={c.previewGroup} role="group" aria-label="Preview the draft">
          <button type="button" className={cx(c.topButton, device === 'mobile' && c.topButtonOn)} title={_('PREVIEW_MOBILE')} aria-label={_('PREVIEW_MOBILE')}
            aria-pressed={device === 'mobile'} onClick={() => showDevice(device === 'mobile' ? null : 'mobile')}>
            <i className="material-icons">smartphone</i>
          </button>
          <button type="button" className={cx(c.topButton, device === 'tablet' && c.topButtonOn)} title={_('PREVIEW_TABLET')} aria-label={_('PREVIEW_TABLET')}
            aria-pressed={device === 'tablet'} onClick={() => showDevice(device === 'tablet' ? null : 'tablet')}>
            <i className="material-icons">tablet_mac</i>
          </button>
          <a className={c.topButton} title={_('PREVIEW_DESKTOP')} aria-label={_('PREVIEW_DESKTOP')} href={previewUrl} target="_blank" rel="noopener noreferrer">
            <i className="material-icons">desktop_windows</i>
          </a>
        </div>
        <button
          type="button"
          className={cx(c.topButton, inspector === 'page' && c.topButtonOn)}
          onClick={togglePageSettings}
          aria-pressed={inspector === 'page'}
          title="Page settings"
          aria-label="Page settings"
        >
          <i className="material-icons">tune</i>
        </button>
        <span className={c.divider} />
        <button type="button" className={c.secondary} onClick={handleSavePage} disabled={isSavingDraft || readOnly} aria-busy={isSavingDraft}>
          {isSavingDraft ? 'Saving…' : 'Save draft'}
        </button>
        <button type="button" className={c.primary} onClick={() => setPublishing({ versionId: null })} disabled={readOnly}>
          Publish…
        </button>
      </div>
    </header>
  );
  const conflictBanner = saveState.status === 'conflict' && (
    <div className={c.conflict} role="alert" data-testid="save-conflict">
      <span>
        Someone else saved this page after you opened it, so your latest changes aren’t saved. Reload to continue from their version; your unsaved changes will be lost.
      </span>
      <button type="button" className={c.conflictButton} onClick={() => window.location.reload()}>Reload page</button>
    </div>
  );
  const Template = Templates[page.template.templateId];
  const controls = (
    <Sidebar
      open={inspector === 'page'}
      onClose={togglePageSettings}
      data={{
        metadata: draft.metadata,
        title: draft.title,
        pageTemplate: draft.template,
        path: draft.path,
        canonicalRel: draft.canonicalRel,
        // Pages without a category (none should exist) open instead of failing.
        category: draft.category ? draft.category.path : ''
      }}
      onChangeVersion={unlessPreviewing(changeVersion)}
      onTemplateSettingsChange={unlessPreviewing(changeTemplateSettings)}
      onChangePageSettings={unlessPreviewing(changePageSettings)}
      onChangeCategory={unlessPreviewing(changeCategory)}
    />
  );
  const previewNote = activeRevision && (
    <div className={c.previewNote}>
      <div>
        {_('PREVIEW_WARNING_FIRST')}
        {' '}
        {activeRevision}
        {' '}
        {_('PREVIEW_WARNING_SECOND')}
        {' '}
        <a
          href={`/admin/page/${page._id}`}
          className={c.inlineLink}
          onClick={resetActiveRevision}
        >
          {_('HERE')}
        </a>
        {' '}
        {_('PREVIEW_WARNING_THIRD')}
      </div>
    </div>
  );
  const sectionPanel = (
    <>
      {panel === 'outline' && (
        <OutlinePanel
          groups={outline}
          selectedId={selectedId}
          readOnly={readOnly}
          canArrange={!isBasicUser}
          onSelect={selectSection}
          onEdit={editSection}
          onDuplicate={duplicateSection}
          onDelete={deleteSection}
          onReorder={reorderSection}
        />
      )}
      {panel === 'insert' && (!isBasicUser || insertAt) && (readOnly
        ? <p className={c.panelNote}>{viewOnly ? previewOnlyMessage : 'You’re viewing an earlier version. Go back to the draft to add sections.'}</p>
        : (
          <InsertPanel
            placement={insertTarget().label}
            targeted={Boolean(insertAt)}
            onClearPlacement={() => setInsertAt(null)}
            onDragItem={readOnly ? undefined : onDragItem}
            focusKey={searchFocus}
            pageId={page._id}
            presets={widgetPresets || []}
            onInsert={insertLatest}
          />
        )
      )}
      {panel === 'pulse' && <PulsePanel pageId={page._id} path={page.path} publishedVersion={page.publishedVersion} />}
      {panel === 'history' && (
        <HistoryPanel
          pageId={page._id}
          publishedVersion={page.publishedVersion}
          canRevert={page.canUserRevert}
          canPublish={page.canUserRequestApproval}
          // Viewing an earlier version, the canvas isn't the draft, so there's nothing to keep.
          restoreKeepsDraft={!isPreviewing}
          onBeforeRestore={async () => {
            setRestoring(true);
            // Let a save in flight finish first, so it isn't rejected as outdated by the restore.
            if (savingRef.current && savePromiseRef.current) await savePromiseRef.current;
            if (!isPreviewing) checkpoint();
            adoptNextServerPage(true);
          }}
          onRestoreFailed={() => { cancelCheckpoint(); adoptNextServerPage(false); setRestoring(false); }}
          onRestored={() => { setRestoring(false); setServerChangedAt(Date.now()); history.push(`/admin/page/${page._id}`); }}
          onPublish={versionToPublish => setPublishing({ versionId: versionToPublish })}
        />
      )}
    </>
  );
  const body = (
    <div className={c.body}>
      {/* Basic editors see Insert only for an add button inside a column or tab. */}
      <EditorRail open={panel} onOpen={openPanel} hidden={isBasicUser && !insertAt ? ['insert'] : []}>{sectionPanel}</EditorRail>
      {/* This must be used as the scroll listener for widgets in the editor */}
      <div
        id="pageEditorScrollable"
        className={`${c.content} scrollbar`}
        onClickCapture={selectFromCanvas}
        onDragOverCapture={onCanvasDragOver}
        onDragLeave={onCanvasDragLeave}
        onDropCapture={onCanvasDrop}
      >
        {previewNote}
        {/* The editor's marks (selection, "New section here", the drop line) are drawn beside the page, not in it:
            the page clips what's outside its edges, and the marks reach a little past a full-width section's. */}
        <div className={c.pageFrame}>
          {/* The page as the site shows it: always light, whatever the admin's theme. */}
          <div className={c.page} ref={setSheet} data-gx-theme="light">
            <EditorContext.Provider
              value={{
                editing: true,
                isBasicUser,
                selectedId,
                onClickAdd: unlessPreviewing(chooseFromCanvas),
                onClickEdit: unlessPreviewing(handleEdit),
                onClickPresets: unlessPreviewing(handleUsePresets),
                onClickDelete: unlessPreviewing(confirmDeleteFromCanvas),
                onClickMove: unlessPreviewing(handleMove),
                onClickCopy: unlessPreviewing(handleCopy),
                onDragAndDrop: unlessPreviewing(handleDragAndDrop)
              }}
            >
              <CanvasBoundary resetKey={JSON.stringify(draft.template)}>
                <DirContainer path={draft.path}>
                  <Template
                    isEditor
                    slots={draft.slots}
                    templateSettings={templateSettingsOf(draft.template)}
                    widgetPresets={widgetPresets}
                    path={draft.path}
                  />
                </DirContainer>
              </CanvasBoundary>
            </EditorContext.Provider>
          </div>
          {insertAt && insertAt.anchor && panel === 'insert' && !dropMark && (
            <div className={c.pickMark} style={{ top: insertAt.anchor.top, left: insertAt.anchor.left, width: insertAt.anchor.width }} data-testid="pick-here">
              <div className={c.pickPill} role="status" aria-label={`New section ${insertAt.label}. Choose a widget in the Insert panel.`}>
                <span className={c.pickTitle}>New section here</span>
                <span className={c.pickHint}>Choose a widget on the left, or</span>
                <button type="button" className={c.pickAi} onClick={() => insertSection(aiSection(), { edit: true })} data-testid="pick-here-ai">
                  Paste from AI tool
                </button>
                <button type="button" className={c.pickClose} onClick={() => setInsertAt(null)} aria-label="Cancel adding a section">
                  <i className="material-icons" aria-hidden>close</i>
                </button>
              </div>
            </div>
          )}
          {dropMark && (
            <div className={c.dropMark} style={{ top: dropMark.top }} data-testid="drop-mark" data-slot={dropMark.slotId} data-index={dropMark.index}>
              <span style={dropMark.top < 18 ? { top: 6 } : undefined}>Add {dropMark.title} {dropMark.label}</span>
            </div>
          )}
          <SelectionMarks
            sectionId={selectedId}
            label={(selectedId && findOutlineItem(selectedId) || {}).title || ''}
            sheet={sheet}
            layoutKey={draft.slots}
          />
        </div>
      </div>
      {device && (
        <DeviceFrames
          src={previewUrl}
          device={device}
          onDevice={showDevice}
          onClose={() => showDevice(null)}
          reloadKey={serverChangedAt}
          note={isPreviewing
            ? `Version ${viewedVersion}, as the site renders it.`
            : `The saved draft, as the site renders it. Updates after each save${isDirty ? ' (unsaved changes show after the next one).' : '.'}`}
        />
      )}
      {inspected && (
        // Its own box: the placeholder after an Edit click stays put wherever the column is scrolled.
        <div className={c.inspectorFrame}>
          <aside className={c.inspector} aria-label="Section" data-testid="section-column" data-toast-beside>
            <div className={c.inspectorHead}>
              <span className={c.inspectorLabel}>{inspecting.subcomponentId ? 'Nested section' : 'Section'}</span>
              <span className={c.inspectorTools}>
                {/* Basic editors edit sections but don't copy or delete them, as on the canvas. */}
                {!readOnly && !isBasicUser && (
                  <RowMenu
                    label="Section actions"
                    testId="section-menu"
                    groups={[
                      inspecting.subcomponentId ? [] : [{
                        key: 'duplicate',
                        label: 'Duplicate section',
                        icon: 'content_copy',
                        onSelect: () => { const item = findOutlineItem(inspecting.componentId); if (item) duplicateSection(item); }
                      }],
                      [{
                        key: 'delete',
                        label: 'Delete section…',
                        icon: 'delete_outline',
                        danger: true,
                        onSelect: () => setPendingDelete({
                          title: nameOf(inspected.instance),
                          run: () => {
                            if (inspecting.subcomponentId) {
                              handleDeleteComponent(inspected.slot, inspected.index, inspecting.subcomponentId);
                              setRemoved({ title: nameOf(inspected.instance), step: historyStep + 1 });
                            } else {
                              const item = findOutlineItem(inspecting.componentId);
                              if (item) deleteSection(item);
                            }
                            setInspector(null);
                          }
                        })
                      }]
                    ]}
                  />
                )}
                <button type="button" className={c.inspectorClose} onClick={() => setInspector(null)} aria-label="Close section">
                  <i className="material-icons">close</i>
                </button>
              </span>
            </div>
            {readOnly ? (
              <p className={c.panelNote} data-testid="inspector-preview-note">{previewOnlyMessage}</p>
            ) : (
            <SectionInspector
              targetKey={`${inspecting.componentId}:${inspecting.subcomponentId || ''}`}
              instance={inspected.instance}
              presetTitle={(inspected.instance.presetId && ((widgetPresets || []).find(p => presetIdsMatch(p._id, inspected.instance.presetId)) || {}).title) || ''}
              isBasicUser={isBasicUser}
              onApply={(props, key) => applyInspectorEdit(inspecting, props, key)}
              onReplace={(component) => {
                const slot = PageEditorUtil.handleSaveComponent(inspected.slot, component, inspected.index);
                updateSlotState(slot);
                const replaced = slot.components[inspected.index];
                if (replaced) { setSelectedId(replaced._id); setInspector({ slotId: inspecting.slotId, componentId: replaced._id, subcomponentId: null }); }
              }}
              onOpenFull={() => showEditWidgetModal(inspecting.slotId, inspected.index, inspecting.subcomponentId || undefined)}
            />
            )}
          </aside>
          {editLoading && (
            <div className={c.inspectorLoading} data-testid="inspector-loading" aria-hidden>
              <div className={c.skeletonPulse} style={{ width: '30%', height: '12px', margin: '16px 0 28px' }} />
              {[45, 100, 35, 100, 50, 80].map((w, i) => (
                <div key={i} className={c.skeletonPulse} style={{ width: `${w}%`, height: i % 2 ? '32px' : '12px', marginBottom: i % 2 ? '24px' : '8px' }} />
              ))}
            </div>
          )}
        </div>
      )}
      {controls}
    </div>
  );
  const leaveConfirm = leaveBlocked && (
    <ConfirmDialog
      danger
      title={leaveBlocked.unsaved ? 'Leave without saving?' : 'Leave without your last changes?'}
      confirmLabel={leaveBlocked.unsaved ? 'Leave without saving' : 'Leave'}
      cancelLabel="Stay"
      onConfirm={() => { const blocked = leaveBlocked; setLeaveBlocked(null); leaveTo(blocked); }}
      onClose={() => setLeaveBlocked(null)}
      testId="leave-dialog"
    >
      {leaveBlocked.unsaved
        ? <p>Your changes since the last save aren’t kept unless you save them. Stay, then click Save draft to keep them.</p>
        : <p>They couldn’t be saved{saveState.message ? `: ${saveState.message}` : ''}. Stay to try Save draft again.</p>}
    </ConfirmDialog>
  );
  const deleteConfirm = pendingDelete && (
    <ConfirmDialog
      danger
      title={`Delete “${pendingDelete.title}”?`}
      confirmLabel="Delete section"
      onConfirm={() => pendingDelete.run()}
      onClose={() => setPendingDelete(null)}
      testId="delete-section-dialog"
    >
      <p>It’s removed from this draft. You can undo it right after, and the live page doesn’t change until you publish.</p>
    </ConfirmDialog>
  );
  // Hidden while a full editor or picker is open: its Undo would change the sections under it.
  const removedNote = removed && removed.step === historyStep && !currentModal && (
    <div className={c.toast} role="status" data-testid="removed-note">
      <span>Removed “{removed.title}”</span>
      <button type="button" className={c.toastButton} onClick={() => { undo(); setRemoved(null); }}>Undo</button>
      <button type="button" className={c.toastClose} onClick={() => setRemoved(null)} aria-label="Dismiss"><i className="material-icons">close</i></button>
    </div>
  );

  const modal = (
    <>
      {
        currentModal === 'edit_widget' &&
        <EditComponent
          instance={getEditorInstance()}
          onChange={handleSaveComponent}
          onClose={resetModal}
          onDelete={() => handleDeleteComponent(draft.slots.find(s => s.slotId === currentModalProps.slotId), currentModalProps.componentPosition)}
          allowReplace
          isBasicUser
        />
      }
      {
        currentModal === 'edit_preset' &&
        <SelectPresets
          widgetId={currentModalProps.widgetId}
          initialPresetId={currentModalProps.initialPresetId}
          onClose={resetModal}
          onChange={handleChangeWidgetPresets}
        />
      }
    </>
  );

  return (
    <div className={c.container}>
      {top}
      {conflictBanner}
      {body}
      {modal}
      {removedNote}
      {deleteConfirm}
      {leaveConfirm}
      {publishing && (
        <PublishSheet
          page={page}
          draft={draft}
          versionId={publishing.versionId}
          versions={versionSummaries}
          lastUpdatedAt={lastUpdatedAt}
          refreshUpdatedAt={async () => { const p = await unchangedServerPage(serverSig); return p ? parsePageUpdatedAt(p.updatedAt) : null; }}
          saving={isSavingDraft}
          nameOf={nameOf}
          onSelectSection={(sectionId) => { setSelectedId(sectionId); setRevealId(sectionId); }}
          onVersionSaved={(updatedAt) => { if (updatedAt) patchDraftMeta(updatedAt); }}
          onDone={(message) => { setPublishing(null); flashMessage(message); }}
          onClose={() => setPublishing(null)}
        />
      )}
    </div>
  );
};

/** "Saving…", "Saved", "Unsaved changes" or a save problem, next to undo/redo. */
function SaveStatus({ state, isDirty, isPreviewing, viewOnly, c }) {
  // Re-render every 30s so "Saved just now" ages.
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  let text = '';
  let tone = c.statusQuiet;
  if (viewOnly) text = 'View only';
  else if (isPreviewing) text = 'Viewing an earlier version';
  else if (state.status === 'saving') text = 'Saving…';
  else if (state.status === 'conflict') { text = 'Not saved: page changed elsewhere'; tone = c.statusBad; }
  else if (state.status === 'error' && isDirty) { text = `Not saved: ${state.message}`; tone = c.statusBad; }
  else if (isDirty) text = 'Unsaved changes';
  else if (state.status === 'saved') text = `Draft saved ${moment(state.at).fromNow()}`;
  return <span className={cx(c.status, tone)} data-testid="save-status" aria-live="polite">{text}</span>;
}

function OnlineUser({ usersOnEdit, user, c }) {
  // State to trigger re-renders
  // eslint-disable-next-line no-unused-vars
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Set up an interval to update the current time every minute
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    // Clear the interval when the component is unmounted
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ display: 'flex', columnGap: '10px' }}>
      {usersOnEdit.map(u => (
        <div
          key={u.userName}
          className={cx(c.userIcon, user?.displayName === u.userName ? c.myProfile : c.otherUserProfile)}
          data-title={u.userName + ' ' + moment(new Date(u.joinedAt)).fromNow()}
        >
          <i className={'material-icons'}>person</i>
        </div>
      ))}
    </div>
  );
}

function DirContainer({ path, children }) {
  // Same rule as the published page (shared/lib/pageLang).
  const { lang, dir } = getPageLangDir(path);

  return (
    <div
      lang={lang}
      dir={dir}
    >
      {children}
    </div>
  )
}

/**
 * Websocket to get a list of users who are editing the page
 */
function useConnectSocketForUsersOnEdit(path, userName, pageId, onChangedByApp) {
  const [usersOnEdit, setUsersOnEdit] = useState([]);
  const changedRef = useRef(onChangedByApp);
  changedRef.current = onChangedByApp;

  useEffect(() => {
    if (!path || !userName) return;

    const socket = io(getAppURL());

    socket.on(WEBSOCKET_MSG_ID.PAGE_CHANGED_BY_APP, (arg) => {
      if (arg && pageId && arg.pageId === String(pageId) && changedRef.current) changedRef.current(String(arg.app || 'An AI app'));
    });

    socket.on('connect', () => {
      // when Editor is up, notify server that the user is on edit
      socket.emit(WEBSOCKET_MSG_ID.JOIN_EDIT,
        {
          path,
          userName,
          socketId: socket.id
        }
      );
    });

    // retrieve the user list who are editing the page
    socket.on(WEBSOCKET_MSG_ID.USERS_ON_EDIT, (arg) => {
      // if the user has multiple tabs open, show the latest user by using reverse().
      // filter out the user who is editing the page under the same path, and not the current user
      let users = arg.reverse().filter(u => u.path === path);
      // multiple tabs with the same page would have duplicate userName, combine to only one
      users = uniqBy(users, 'userName');

      setUsersOnEdit(users);
    });

    return () => {
      socket.disconnect();
    };
  }, [path, userName, pageId]);

  return usersOnEdit;
}

/* =============================================================================
=    Redux
============================================================================= */
const mapStateToProps = () => (state) => ({
  isBasicUser: !!state.app.user.isBasic,
  user: state.app.user,
});

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '-200% 0' },
    '100%': { backgroundPosition: '200% 0' }
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    background: S.wall,
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000
  },
  skeletonTopBar: {
    height: '52px',
    flexShrink: 0,
    boxSizing: 'border-box',
    background: S.chrome,
    borderBottom: `1px solid ${S.line}`,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px'
  },
  skeletonBreadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  skeletonSlash: {
    color: S.muted,
    fontSize: '14px'
  },
  skeletonBody: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  skeletonRail: {
    width: 64,
    flexShrink: 0,
    background: S.chrome,
    borderRight: `1px solid ${S.line}`
  },
  skeletonPanel: {
    width: 300,
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: '20px 16px',
    background: S.chrome,
    borderRight: `1px solid ${S.line}`
  },
  skeletonContent: {
    flex: 1,
    padding: '20px 24px',
    overflow: 'hidden'
  },
  skeletonPage: {
    height: '100%',
    background: S.field,
    boxShadow: '0 1px 3px rgba(23, 26, 32, 0.12)'
  },
  skeletonPulse: {
    background: `linear-gradient(90deg, ${S.line} 25%, ${S.chromeHover} 50%, ${S.line} 75%)`,
    backgroundSize: '200% 100%',
    animation: '$shimmer 1.5s ease-in-out infinite',
    borderRadius: '4px'
  },
  container: {
    padding: '0',
    flexGrow: 2,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    fontFamily: S.ui,
    color: S.fg,
    background: S.wall
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    minHeight: '52px',
    boxSizing: 'border-box',
    padding: '8px 12px 8px 16px',
    background: S.chrome,
    borderBottom: `1px solid ${S.line}`,
    fontSize: '14px'
  },
  crumbs: {
    display: 'flex',
    alignItems: 'baseline',
    minWidth: 0,
    whiteSpace: 'nowrap'
  },
  crumb: {
    color: S.muted,
    textDecoration: 'none',
    '&:hover': { color: S.fg }
  },
  slash: {
    color: S.fieldLine,
    margin: '0 8px'
  },
  title: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  path: {
    marginLeft: '10px',
    fontFamily: S.mono,
    fontSize: '12px',
    color: S.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  topRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0
  },
  divider: {
    width: '1px',
    height: '22px',
    margin: '0 6px',
    background: S.line
  },
  previewGroup: {
    display: 'flex',
    gap: '2px'
  },
  topButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    color: S.fg,
    cursor: 'pointer',
    textDecoration: 'none',
    '& .material-icons': { fontSize: '20px' },
    '&:hover:not(:disabled), &:focus-visible': { background: S.chromeHover },
    '&:disabled': { opacity: 0.35, cursor: 'default' }
  },
  topButtonOn: {
    background: S.field,
    boxShadow: `0 0 0 1px ${S.fieldLine}`
  },
  secondary: {
    height: '32px',
    padding: '0 14px',
    fontFamily: S.ui,
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '6px',
    border: `1px solid ${S.fieldLine}`,
    background: S.field,
    color: S.fg,
    cursor: 'pointer',
    '&:hover:not(:disabled)': { borderColor: S.fg },
    '&:disabled': { color: S.muted, cursor: 'default' }
  },
  primary: {
    height: '32px',
    padding: '0 16px',
    fontFamily: S.ui,
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '6px',
    border: `1px solid ${S.fg}`,
    background: S.fg,
    color: S.field,
    cursor: 'pointer',
    '&:hover:not(:disabled)': { background: S.fgActive },
    '&:disabled': { opacity: 0.4, cursor: 'default' }
  },
  status: {
    fontSize: '13px',
    marginRight: '6px',
    maxWidth: '320px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  statusQuiet: { color: S.muted },
  statusBad: { color: S.red, fontWeight: 600 },
  conflict: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '10px 16px',
    background: '#FDF1E6',
    color: S.clayInk,
    fontSize: '13px',
    lineHeight: 1.5,
    borderBottom: '1px solid #E8C9A6',
    [DARK]: { background: S.claySoft, borderBottomColor: S.line }
  },
  conflictButton: {
    flexShrink: 0,
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '6px',
    border: `1px solid ${S.clayInk}`,
    background: S.field,
    color: S.clayInk,
    cursor: 'pointer'
  },
  body: {
    flexGrow: 2,
    display: 'flex',
    minHeight: 0,
    height: 'calc(100% - 52px)'
  },
  content: {
    flexGrow: 2,
    minWidth: 0,
    overflow: 'auto',
    position: 'relative',
    padding: '20px 24px 48px',
    backgroundColor: S.wall,
    backgroundImage: `linear-gradient(${S.wallGrid} 1px, transparent 1px), linear-gradient(90deg, ${S.wallGrid} 1px, transparent 1px)`,
    backgroundSize: '24px 24px',
    // Light scrollbar for the canvas (the global .scrollbar one is dark).
    '&.scrollbar::-webkit-scrollbar': { width: '12px', backgroundColor: S.wall },
    '&.scrollbar::-webkit-scrollbar-track': { WebkitBoxShadow: 'none', boxShadow: 'none', backgroundColor: S.wall },
    '&.scrollbar::-webkit-scrollbar-thumb': { backgroundColor: S.muted, border: `3px solid ${S.wall}`, borderRadius: '6px' }
  },
  // The page sits on the wall like a sheet; no transform, so fixed-position widgets still work.
  // The page and, over it, the editor's marks: same top-left corner, so the marks' positions (measured from the
  // page) need no change. A column so the page still fills the canvas's height.
  pageFrame: { position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column' },
  page: {
    position: 'relative',
    flex: '1 0 auto',
    // The page stands in for the browser window, as on the site: what a widget positions absolutely or
    // fixed (top: 0, a sticky header) is placed within it, anything wider or higher (100vw, top: -60px)
    // is cut off at its edges, and its z-index stays inside, so nothing covers the editor around it.
    contain: 'paint',
    background: S.field,
    boxShadow: '0 1px 3px rgba(23, 26, 32, 0.12), 0 8px 24px rgba(23, 26, 32, 0.08)'
  },
  inspectorFrame: {
    position: 'relative',
    display: 'flex',
    width: '360px',
    flexShrink: 0
  },
  // Covers the section column (and its sticky head) while it shows; a fresh copy each time, from the top.
  inspectorLoading: {
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    boxSizing: 'border-box',
    padding: '0 16px',
    background: S.chrome,
    borderLeft: `1px solid ${S.line}`
  },
  inspector: {
    flex: '1 1 auto',
    minWidth: 0,
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: '0 16px 24px',
    background: S.chrome,
    borderLeft: `1px solid ${S.line}`
  },
  inspectorHead: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 0 10px',
    background: S.chrome
  },
  inspectorLabel: {
    fontFamily: S.label,
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: S.muted
  },
  inspectorTools: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px'
  },
  '@keyframes gxPickIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  // Where the next section goes, picked with an "Add widget" button: a line like the drag one, and a
  // small dark label on it (what happens now, and the usual choice).
  pickMark: {
    position: 'absolute',
    height: 0,
    zIndex: 31,
    pointerEvents: 'none',
    borderTop: `2px solid ${S.blue}`,
    transform: 'translateY(-1px)',
    animation: '$gxPickIn 150ms ease-out'
  },
  pickPill: {
    position: 'absolute',
    left: '50%',
    top: 0,
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 30,
    padding: '0 4px 0 12px',
    borderRadius: 6,
    background: '#171A20',
    color: '#FFFFFF',
    fontFamily: S.ui,
    fontSize: 12.5,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 14px rgba(23, 26, 32, 0.22)',
    pointerEvents: 'auto'
  },
  pickTitle: { fontWeight: 600 },
  pickHint: { color: 'rgba(255, 255, 255, 0.62)' },
  pickAi: {
    all: 'unset',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#FFFFFF',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    textDecorationColor: 'rgba(255, 255, 255, 0.45)',
    '&:hover, &:focus-visible': { textDecorationColor: '#FFFFFF' }
  },
  pickClose: {
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    marginLeft: 2,
    padding: 3,
    borderRadius: 4,
    color: 'rgba(255, 255, 255, 0.65)',
    '& .material-icons': { fontSize: 16 },
    '&:hover, &:focus-visible': { background: 'rgba(255, 255, 255, 0.12)', color: '#FFFFFF' }
  },
  // Where a section dragged in from the Insert panel will land.
  dropMark: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 0,
    zIndex: 30,
    pointerEvents: 'none',
    borderTop: `3px solid ${S.blue}`,
    transform: 'translateY(-1.5px)',
    '& span': {
      position: 'absolute',
      left: 12,
      top: -26,
      padding: '4px 10px',
      borderRadius: 999,
      background: S.blue,
      color: '#fff',
      fontFamily: S.ui,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      boxShadow: '0 2px 6px rgba(23, 26, 32, 0.2)'
    }
  },
  inspectorClose: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: '18px' },
    '&:hover': { background: S.chromeHover, color: S.fg }
  },
  panelNote: {
    margin: 0,
    fontSize: '13px',
    color: S.muted
  },
  previewNote: {
    width: '250px',
    backgroundColor: S.fg,
    color: S.field,
    position: 'absolute',
    top: '28px',
    left: '32px',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '10',
    borderRadius: '6px',
    fontSize: '13px'
  },
  inlineLink: {
    display: 'inline',
    color: S.field,
    cursor: 'pointer'
  },
  toast: {
    position: 'fixed',
    left: '50%',
    bottom: '24px',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 8px 8px 16px',
    borderRadius: '8px',
    background: S.fg,
    color: S.field,
    fontSize: '14px',
    boxShadow: '0 8px 24px rgba(23, 26, 32, 0.25)',
    [DARK]: { boxShadow: A.pop }
  },
  toastButton: {
    border: 0,
    background: 'transparent',
    color: S.field,
    fontFamily: S.ui,
    fontSize: '14px',
    fontWeight: 700,
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  toastClose: {
    display: 'inline-flex',
    border: 0,
    background: 'transparent',
    color: S.wall,
    cursor: 'pointer',
    '& .material-icons': { fontSize: '18px' }
  },
  userIcon: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '50%',
    padding: '3px',
    '& .material-icons': { fontSize: '18px' },
    '&::after': {
      backgroundColor: S.fg,
      color: S.field,
      padding: '5px 10px',
      borderRadius: '4px',
      content: 'attr(data-title)',
      display: 'none',
      position: 'absolute',
      bottom: '-40px',
      right: 0,
      whiteSpace: 'nowrap',
      fontFamily: S.ui,
      fontSize: '12px',
      zIndex: 13, // z-index greater than editor
    },
    '&:hover::after': {
      display: 'block',
    },
  },
  myProfile: {
    color: S.fg,
    border: `2px solid ${S.fieldLine}`,
  },
  otherUserProfile: {
    color: S.green,
    border: `2px solid ${S.green}`,
  },
};

export default injectSheet(styles)(PageEditor);
